import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CalendarIcon, ClockIcon, Users, SaveIcon, XIcon, AlertTriangleIcon } from 'lucide-react';
import type { CalendarDay, ScheduleType } from '@/types/schedule.types';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDays: CalendarDay[];
  onUpdate: (dayIds: string[], updates: Partial<CalendarDay>) => Promise<void>;
  templateName: string;
}

const scheduleTypes: { value: ScheduleType; label: string }[] = [
  { value: 'working', label: 'Рабочий день' },
  { value: 'day_off', label: 'Выходной' },
  { value: 'holiday', label: 'Праздник' },
  { value: 'reduced', label: 'Сокращённый' }
];

interface BulkEditData {
  schedule_type?: ScheduleType;
  shift_start?: string;
  shift_end?: string;
  is_working_day?: boolean;
}

export const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  selectedDays,
  onUpdate,
  templateName,
}) => {
  const [editData, setEditData] = useState<BulkEditData>({});
  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setEditData({});
      setEnabledFields({});
    }
  }, [isOpen]);

  const handleFieldToggle = (field: keyof BulkEditData, enabled: boolean) => {
    setEnabledFields(prev => ({ ...prev, [field]: enabled }));

    if (!enabled) {
      // Remove field from edit data if disabled
      const newData = { ...editData };
      delete newData[field];
      setEditData(newData);
    }
  };

  const handleScheduleTypeChange = (value: ScheduleType) => {
    setEditData(prev => ({
      ...prev,
      schedule_type: value,
      is_working_day: value === 'working' || value === 'reduced'
    }));

    // Auto-enable working day field
    setEnabledFields(prev => ({ ...prev, is_working_day: true }));
  };

  const handleSave = async () => {
    if (!Object.keys(editData).length) return;

    setIsLoading(true);
    try {
      const dayIds = selectedDays.map(day => day.id);
      await onUpdate(dayIds, editData);
      onClose();
    } catch (error) {
      console.error('Failed to update days:', error);
      // Could add toast notification here
    } finally {
      setIsLoading(false);
    }
  };

  const getDateRangeText = () => {
    if (selectedDays.length === 0) return '';
    if (selectedDays.length === 1) {
      return new Date(selectedDays[0].date).toLocaleDateString('ru-RU');
    }

    const dates = selectedDays.map(day => new Date(day.date)).sort((a, b) => a.getTime() - b.getTime());
    const start = dates[0].toLocaleDateString('ru-RU');
    const end = dates[dates.length - 1].toLocaleDateString('ru-RU');

    return `${start} — ${end}`;
  };

  const getAffectedDaysInfo = () => {
    const typeCount = selectedDays.reduce((acc, day) => {
      acc[day.schedule_type] = (acc[day.schedule_type] || 0) + 1;
      return acc;
    }, {} as Record<ScheduleType, number>);

    return Object.entries(typeCount).map(([type, count]) => {
      const typeInfo = scheduleTypes.find(t => t.value === type);
      return { type: typeInfo?.label || type, count };
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Массовое редактирование
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Информация о выборке */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-sm">
                  Выбрано дней: {selectedDays.length}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {getDateRangeText()}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Шаблон: {templateName}
                </p>
              </div>
              <CalendarIcon className="h-5 w-5 text-blue-500" />
            </div>

            {/* Распределение по типам */}
            <div className="mt-3 flex flex-wrap gap-2">
              {getAffectedDaysInfo().map(({ type, count }) => (
                <Badge key={type} variant="outline" className="text-xs">
                  {type}: {count}
                </Badge>
              ))}
            </div>
          </div>

          <Separator />

          {/* Поля для редактирования */}
          <div className="space-y-4">
            {/* Тип рабочего дня */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="schedule_type"
                  checked={enabledFields.schedule_type || false}
                  onCheckedChange={(checked: boolean) => handleFieldToggle('schedule_type', checked)}
                />
                <Label htmlFor="schedule_type" className="text-sm font-medium">
                  Изменить тип дня
                </Label>
              </div>

              {enabledFields.schedule_type && (
                <Select
                  value={editData.schedule_type}
                  onValueChange={handleScheduleTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите тип дня" />
                  </SelectTrigger>
                  <SelectContent>
                    {scheduleTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Рабочие часы */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="working_hours"
                  checked={enabledFields.shift_start || enabledFields.shift_end || false}
                  onCheckedChange={(checked: boolean) => {
                    handleFieldToggle('shift_start', checked);
                    handleFieldToggle('shift_end', checked);
                  }}
                />
                <Label htmlFor="working_hours" className="text-sm font-medium flex items-center gap-1">
                  <ClockIcon className="h-4 w-4" />
                  Изменить рабочие часы
                </Label>
              </div>

              {(enabledFields.shift_start || enabledFields.shift_end) && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-500">Начало</Label>
                    <Input
                      type="time"
                      value={editData.shift_start || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, shift_start: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Окончание</Label>
                    <Input
                      type="time"
                      value={editData.shift_end || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, shift_end: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Предупреждение */}
          {Object.keys(editData).length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">Внимание</p>
                  <p className="mt-1">
                    Изменения будут применены ко всем {selectedDays.length} выбранным дням.
                    Это действие нельзя будет отменить.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            <XIcon className="h-4 w-4 mr-1" />
            Отмена
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !Object.keys(editData).length}
            className="min-w-24"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
            ) : (
              <SaveIcon className="h-4 w-4 mr-1" />
            )}
            {isLoading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};